import { createClient } from "@connectrpc/connect";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { WorkspaceService } from "@/gen/omnios/v1/workspace_connect.js";
import { transport } from "@/lib/grpc";

import { useTranslation } from "react-i18next";

const client = createClient(WorkspaceService, transport);

export function ReportProblemDialog() {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	// Listen for custom event from UserMenu dropdown
	useEffect(() => {
		const handler = () => setOpen(true);
		window.addEventListener("omnios:report-problem", handler);
		return () => window.removeEventListener("omnios:report-problem", handler);
	}, []);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");

	const mutation = useMutation({
		mutationFn: () =>
			client.reportProblem({
				title: title.trim(),
				description: description.trim(),
			}),
		onSuccess: (res) => {
			toast.success(t("report_sent"), {
				description: res.issueUrl ? t("report_link_copied") : undefined,
			});
			if (res.issueUrl) {
				navigator.clipboard.writeText(res.issueUrl).catch(() => {});
			}
			setOpen(false);
			setTitle("");
			setDescription("");
		},
		onError: () => {
			toast.error(t("report_error"));
		},
	});

	return (
		<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{t("report_problem")}</DialogTitle>
						<DialogDescription>{t("report_problem_desc")}</DialogDescription>
					</DialogHeader>

					<form
						onSubmit={(e) => {
							e.preventDefault();
							mutation.mutate();
						}}
						className="space-y-4"
					>
						<div className="space-y-2">
							<Label htmlFor="report-title">{t("report_subject")}</Label>
							<Input
								id="report-title"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								placeholder={t("report_subject_placeholder")}
								required
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="report-desc">
								{t("report_description_label")}
							</Label>
							<Textarea
								id="report-desc"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder={t("report_detail_placeholder")}
								rows={5}
							/>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => setOpen(false)}
							>
								{t("cancel")}
							</Button>
							<Button
								type="submit"
								disabled={!title.trim() || mutation.isPending}
							>
								{mutation.isPending ? t("report_sending") : t("report_send")}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
		</Dialog>
	);
}
